import {
  type ExpertCreateInput,
  SegmentationTypeWithGroup,
} from '@/client/services/api/graphql/gql/graphql';

import type { ExpertFormData } from '../forms/types';
import { createExpertMutation } from '../api/mutations/createExpertMutation';
import { graphQLClient } from '@/client/services/api/clients/graphqlClient';
import { useMutation } from '@tanstack/react-query';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { useExpertsStore } from '../expertsStore';

export const useCreateExpert = () => {
  const { t } = useTranslation();
  const { setToast } = useToastStore();

  const { setUpdatingExpertModalIsOpen } = useExpertsStore();

  const segmentationTypeToEnumMap: Record<string, SegmentationTypeWithGroup> = {
    location: SegmentationTypeWithGroup.Location,
    department: SegmentationTypeWithGroup.Department,
    group: SegmentationTypeWithGroup.Group,
    team: SegmentationTypeWithGroup.Team,
    grade: SegmentationTypeWithGroup.Grade,
    org_level: SegmentationTypeWithGroup.OrgLevel,
  };

  interface CreateExpertMutationVariables {
    formData: ExpertFormData;
  }

  const createExpert = useMutation({
    mutationFn: async ({ formData }: CreateExpertMutationVariables) => {
      const { color, description, enabled, name, segmentation, resources } = formData;

      const createExpertPayload: ExpertCreateInput = {
        color,
        description,
        enabled,
        name,
        segmentation: segmentation.map((segment) => ({
          segmentationId: segment.segmentationId,
          segmentationType: segmentationTypeToEnumMap[segment.segmentationType],
        })),
        resources: resources.map((resource) => ({
          addedBy: resource.addedBy,
          extensionName: resource.extensionName,
          extensionType: resource.extensionType,
          path: resource.path,
          source: resource.source,
          sourceId: resource.sourceId,
          downloadLink: resource.downloadLink,
          isFolder: resource.isFolder,
          fileCount: resource.fileCount,
          parentFolder: resource.parentFolder,
        })),
      };

      const data = await graphQLClient.request(createExpertMutation, {
        expert: createExpertPayload,
      });

      return data.createExpert;
    },
    onSuccess: (successData, { formData }) => {
      setToast({
        show: true,
        status: 'success',
        title: formData.enabled
          ? t('admin.aiAgent.experts.createSuccess.published')
          : t('admin.aiAgent.experts.createSuccess.draft'),
      });

      setUpdatingExpertModalIsOpen(true);
    },
    onError: () => {
      setToast({
        show: true,
        status: 'error',
        title: t('admin.aiAgent.experts.createError'),
      });
    },
  });

  return {
    createExpert,
  };
};
